<script>
import { useMarkdownify } from '../modules/markdownify.js';

const { markdownify } = useMarkdownify();

export default {
  name: 'AlgoliaCards',

  props: {
    locale: {
      type: String,
      default: 'en-US',
    },

    results: {
      type: Object,
      default: () => {},
    },

    userData: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      keyTimeout: null,
    };
  },

  computed: {
    getUserData() {
      if (this.results.userData) {
        return this.results.userData[this.locale.replace('-', '_')]; // only show localized banners
      }
      return this.results.userData;
    },
  },

  watch: {
    results(value) {
      /*
        clear the timeout and wait for user to stop typing
        before sending GA and utag values
      */
      clearTimeout(this.keyTimeout);
      this.keyTimeout = setTimeout(() => {
        // after search set utag value for DI team to key off of
        window.utag.data.algoliaQueryId = value.queryID;

        // push search value to GA
        if (window.ga) {
          window.ga('gprod.send', 'event', {
            eventCategory: 'Algolia',
            eventAction: 'Search',
            eventLabel: value.query,
            transport: 'beacon',
          });
        }
      }, 3000); // 3 second timeout before sending search result to google
    },
  },

  methods: {
    chooseResult({ result, resultIndex }) {
      // prefix = page (0-based) * pageSize, position is 1-based
      const position = ((this.results.currentPage - 1) * this.results.pageSize) + resultIndex + 1;
      // eslint-disable-next-line
      const queryID = this.results.queryID;
      if (window.aa) {
        window.aa('clickedObjectIDsAfterSearch', {
          queryID, eventName: 'Clicked item', index: 'eCommerce', objectIDs: [result.objectID], positions: [position],
        });
      }
    },

    getCuratedContent(text) {
      return markdownify(text);
    },
  },
};
</script>

<template>
  <div
    class="cards"
  >
    <a
      v-for="(item, index) in getUserData"
      :key="index"
      class="card"
      :href="item.link"
    >
      <div
        v-if="results.currentPage === 1"
        class="card__banner"
      >
        <h2
          class="card__banner__heading"
          v-html="getCuratedContent(item.heading)"
        />
        <p
          class="card__banner__text"
          v-html="getCuratedContent(item.text)"
        />
      </div>
      <span class="card__banner__cta">
        {{ item.cta }}
        <svg
          class="card__banner__cta__svg"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          fill-rule="evenodd"
          clip-rule="evenodd"
          stroke-linejoin="round"
          stroke-miterlimit="1.414"
        ><path
          d="M5.88 4.12L13.76 12l-7.88 7.88L8 22l10-10L8 2 5.88 4.12z"
          fill-rule="nonzero"
        /></svg>
      </span>
    </a>

    <a
      v-for="(result, resultIndex) in results.hits"
      :key="resultIndex"
      :href="result.productVariationEcommURL ? result.productVariationEcommURL : result.productEcommURL"
      class="card"
      @click="chooseResult({result, resultIndex})"
    >
      <div>
        <img
          :src="result.productImageLarge"
          class="card__image"
          :alt="result.productTitleParent"
        >
        <h2
          class="card__name"
          v-html="getCuratedContent(result.productTitleParent)"
        />
        <span
          class="card__description"
          v-html="getCuratedContent(result.productCaption)"
        />
      </div>
    </a>
  </div>
</template>

<style lang="scss" scoped>
.cards {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @include font-primary();
  @include font-primary-weight-regular();
  background: $color-white;
  border-radius: 3px; // material
  box-shadow: 0 1px 1px 0 rgba(60, 64, 67, 0.08), 0 1px 3px 1px rgba(60, 64, 67, 0.16); // material
  transition: box-shadow 135ms cubic-bezier(0.4, 0, 0.2, 1), width 235ms cubic-bezier(0.4, 0, 0.2, 1); // material
  padding: 1rem;
  width: calc(100% - 2rem);
  margin: 1rem;
  min-height: 0;
  @include breakpoint('xs') {
    width: calc(50% - 2rem);
  }
  @include breakpoint('sm') {
    width: calc(calc(100% / 3) - 2rem);
  }
  @include breakpoint('lg') {
    width: calc(25% - 2rem);
  }

  &:deep(sup) {
    // override the insignia superscripts as the sups are too high
    /* stylelint-disable */
    top: 0 !important;
    /* stylelint-enable */
  }

  &:link,
  &:visited,
  &:hover,
  &:active {
    color: $color-black;
    text-decoration: none;
    cursor: pointer;
  }

  &:hover {
    @include make-drop-shadow();
  }

  &__image {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    // so IE flexbox doesn't freak out ugh
    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      max-height: 300px;
    }
  }

  &__name {
    font-size: 0.9rem;
    color: $color-black;
    margin: 0;
    margin-bottom: 0.5rem;
    word-break: break-word;
    @include font-primary-weight-bold();
  }

  &__description {
    font-size: 0.9rem;
    color: $color-black;
    margin: 0;
  }

  &__price {
    display: block;
    font-size: 0.9rem;
    color: $color-gray-60;
    margin-top: 1rem;
  }

  &__banner {

    &__heading {
      font-size: 1.5rem;
      letter-spacing: 0.02rem;
      text-transform: uppercase;
    }

    &__text {
      line-height: 1.5rem;
    }

    &__cta {
      font-size: 0.9rem;
      @include font-primary-weight-bold();
      text-transform: uppercase;
      text-align: end;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-top: 2rem;

      &__svg {
        margin-inline-start: 0.5rem;
        height: 0.9rem;
        width: 0.9rem;
      }
    }
  }
}
</style>
